import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { useContribuyentesSearch } from "../hooks/useContribuyentesSearch";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Calculator, Trash2, Plus, Edit, Ban, Search, Download, MessageCircle, FileSearch, FileSpreadsheet, FileText, FileJson, RotateCcw, Info as InfoIcon, X, Building2, Zap, Mail } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPalmaLogo, drawPalmaFooter, fechaHoraVE } from "../lib/pdfFooter";
import { ContribuyenteCombobox } from "../components/ContribuyenteCombobox";

export default function LiquidacionesPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <div className="space-y-6" data-testid="liquidaciones-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3"><Calculator className="w-8 h-8 text-orange-500" />Liquidaciones</h1>
        <p className="text-green-700/70 mt-1">Registro de declaraciones tributarias</p>
      </div>
      <DatosDeclaracion auth={auth} API={API} />
    </div>
  );
}

const MESES_DECL = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const ANIOS_DECL = (() => { const y = new Date().getFullYear(); const a=[]; for (let i=y+1;i>=2015;i--) a.push(i); return a; })();

function computePendientes(ultimoMes, ultimoAnio) {
  // Genera lista cronológica de períodos pendientes desde el mes siguiente al último pagado hasta el mes/año actual
  const hoy = new Date();
  const finY = hoy.getFullYear();
  const finM = hoy.getMonth() + 1; // 1-12
  let y, m;
  if (ultimoMes && ultimoAnio) {
    const idx = MESES_DECL.indexOf(ultimoMes);
    const ultM = idx >= 0 ? idx + 1 : 1;
    if (ultM === 12) { y = Number(ultimoAnio) + 1; m = 1; }
    else { y = Number(ultimoAnio); m = ultM + 1; }
  } else {
    // Sin histórico: arrancar desde enero del año actual
    y = finY; m = 1;
  }
  const out = [];
  while (y < finY || (y === finY && m <= finM)) {
    out.push({ mes: MESES_DECL[m - 1], anio: y, key: `${y}-${String(m).padStart(2,"0")}` });
    if (m === 12) { m = 1; y++; } else m++;
    if (out.length > 240) break; // safety
  }
  return out;
}

// Convierte "YYYY-MM" -> { mes: "Marzo", anio: 2026 }
function ymToMesAnio(ym) {
  if (!ym || typeof ym !== "string") return null;
  const parts = ym.split("-");
  if (parts.length !== 2) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m || m < 1 || m > 12) return null;
  return { mes: MESES_DECL[m - 1], anio: y };
}

// Dado un array de declaraciones y el par (ultimoMes, ultimoAnio) del contribuyente,
// devuelve el ÚLTIMO período efectivamente cubierto por pagos validados (el más reciente).
// Considera tanto las declaraciones en estatus "Pagada" (periodos_cubiertos)
// como el snapshot manual del contribuyente. Devuelve { mes, anio } o null.
function calcularUltimoPeriodoPagado(declaracionesPagadas, ultimoMesManual, ultimoAnioManual) {
  const cubiertos = new Set();
  (declaracionesPagadas || []).forEach(d => {
    (d.periodos_cubiertos || []).forEach(p => cubiertos.add(p));
    if (d.mes_anio_declaracion) cubiertos.add(d.mes_anio_declaracion);
  });
  // Añadir snapshot manual si existe
  if (ultimoMesManual && ultimoAnioManual) {
    const idx = MESES_DECL.indexOf(ultimoMesManual);
    if (idx >= 0) cubiertos.add(`${ultimoAnioManual}-${String(idx + 1).padStart(2, "0")}`);
  }
  if (cubiertos.size === 0) return { mesAnio: null, cubiertos };
  const ordenados = [...cubiertos].filter(x => /^\d{4}-\d{2}$/.test(x)).sort();
  const ultimo = ordenados[ordenados.length - 1];
  return { mesAnio: ymToMesAnio(ultimo), cubiertos };
}

const emptyDecl = {
  contribuyente_id: "", clase_contribuyente: "", rif: "", nombre_razon_social: "", lae: "",
  codigo_catastral: "", fecha: new Date().toISOString().slice(0,10), mes_anio_declaracion: "",
  oficina: "", con_actividad_economica: false, estatus_pago: "Sin pagar", estatus: "Registrada",
  tipos_impuestos: [], lineas_impuestos: [],
  descuento: 0, reintegro: 0, retencion: 0, multa: 0, divisas: 0, divisas_euro: 0, total: 0,
};

// ============================== Exportación de Liquidaciones ==============================
function declaracionToRow(d) {
  return {
    "N° Planilla": d.nro_planilla || "",
    "Fecha": d.fecha || "",
    "RIF/Cédula": d.rif || "",
    "Contribuyente": d.nombre_razon_social || "",
    "LAE": d.lae || "",
    "Clase": d.clase_contribuyente || "",
    "Período": d.mes_anio_declaracion || "",
    "Total (Bs.)": Number(d.total || 0),
    "Descuento": Number(d.descuento || 0),
    "Reintegro": Number(d.reintegro || 0),
    "Retención": Number(d.retencion || 0),
    "Multa": Number(d.multa || 0),
    "Divisas USD": Number(d.divisas || 0),
    "Divisas EUR": Number(d.divisas_euro || 0),
    "Estatus Pago": d.estatus_pago || "",
    "Estatus": d.estatus || "",
    "Nº Comprobante Pago": d.nro_comprobante || "",
    "Fecha Pago": d.fecha_pago || "",
    "Fecha Registro": (d.created_at || "").slice(0, 10),
  };
}

function exportarLiquidaciones({ formato, rows }) {
  if (!rows.length) { toast.error("Nada que exportar con los filtros actuales"); return; }
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `liquidaciones_${stamp}`;

  if (formato === "excel") {
    const data = rows.map(declaracionToRow);
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Liquidaciones");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Excel descargado (${rows.length} declaraciones)`);
  } else if (formato === "json") {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.json`; a.click(); URL.revokeObjectURL(url);
    toast.success(`JSON descargado (${rows.length} declaraciones)`);
  } else if (formato === "pdf") {
    loadPalmaLogo().then(logoDataURL => {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45);
      doc.text("SISAUR · Liquidaciones", 40, 40);
      doc.setFontSize(11); doc.setTextColor(80);
      doc.text(`Generado: ${fecha} ${hora} · Total: ${rows.length} declaraciones`, 40, 58);
      const total = rows.reduce((s, d) => s + Number(d.total || 0), 0);
      const pagadas = rows.filter(d => d.estatus_pago === "Pagado").length;
      doc.text(`Pagadas: ${pagadas} · Sin pagar: ${rows.length - pagadas} · Monto total: Bs. ${total.toLocaleString("es-VE", { maximumFractionDigits: 2 })}`, 40, 74);

      const head = [["N° Planilla", "Fecha", "RIF", "Contribuyente", "Período", "Total (Bs.)", "Pago", "Estatus"]];
      const body = rows.map(d => [
        d.nro_planilla || "—",
        d.fecha || "—",
        d.rif || "—",
        (d.nombre_razon_social || "—").slice(0, 40),
        d.mes_anio_declaracion || "—",
        Number(d.total || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 }),
        d.estatus_pago || "—",
        d.estatus || "—",
      ]);
      autoTable(doc, {
        head, body, startY: 90,
        margin: { bottom: 60 },
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [20, 83, 45], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 5: { halign: "right" } },
        didDrawPage: (data) => drawPalmaFooter(doc, { logoDataURL, fecha, hora, pageNumber: data.pageNumber }),
      });
      doc.save(`${filename}.pdf`);
      toast.success(`PDF descargado (${rows.length} declaraciones)`);
    });
  }
}

function DatosDeclaracion({ auth, API }) {
  const { hasPermission } = useAuth();
  const canEditAjustes = hasPermission("liquidaciones.editar_ajustes");
  const [items, setItems] = useState([]);
  // Filtros multi-columna (búsqueda individual por campo). Se combinan AND.
  const [colFilters, setColFilters] = useState({
    nro_planilla: "", fecha: "", rif: "", contribuyente: "",
    periodo: "", total: "", pago: "ALL", estatus: "ALL",
  });
  const [tiposImp, setTiposImp] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDecl);
  const [lineaInput, setLineaInput] = useState({ impuesto_tasa: "", ingreso_bruto: "", importe: "", sub_total: "" });
  const [mesesPendientes, setMesesPendientes] = useState([]);
  const [periodosSeleccionados, setPeriodosSeleccionados] = useState([]);
  const [cotizacionActiva, setCotizacionActiva] = useState(null);
  const [tarifasAseo, setTarifasAseo] = useState([]);
  const [contribSel, setContribSel] = useState(null); // contribuyente actualmente cargado en el form
  const [creditoDisponible, setCreditoDisponible] = useState(0); // saldo a favor Bs
  const { contribuyentes, setContribuyentes, contribSearch, setContribSearch } = useContribuyentesSearch({ initialLimit: 200 });
  const [contribSedes, setContribSedes] = useState([]); // sedes del contribuyente seleccionado (multi-sede)
  // ====== Multi-sede: dialog para generar planillas mensuales de TODAS las sedes de un contribuyente ======
  const [msOpen, setMsOpen] = useState(false);
  const [msContrib, setMsContrib] = useState(null);
  const [msSearch, setMsSearch] = useState("");
  const [msResults, setMsResults] = useState([]);
  const [msSedes, setMsSedes] = useState([]);
  const [msDesde, setMsDesde] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [msHasta, setMsHasta] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [msLoading, setMsLoading] = useState(false);
  const [msResult, setMsResult] = useState(null);

  // Resuelve un contribuyente por id: primero mira la lista local, si no lo halla lo trae del backend
  // y lo agrega al cache. Necesario porque solo cargamos 200 contribuyentes iniciales.
  const getContribById = async (id) => {
    const local = contribuyentes.find(x => x.id === id);
    if (local) return local;
    try {
      const r = await axios.get(`${API}/contribuyentes/${id}`, auth);
      const c = r.data;
      if (c) setContribuyentes(prev => prev.find(x => x.id === c.id) ? prev : [...prev, c]);
      return c;
    } catch { return null; }
  };

  const load = () => axios.get(`${API}/liquidaciones/datos-declaracion`, { ...auth, params: {} }).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ====== Multi-sede handlers ======
  const msSearchContrib = async (q) => {
    if (!q || q.length < 2) { setMsResults([]); return; }
    try {
      const r = await axios.get(`${API}/contribuyentes`, { ...auth, params: { search: q, clase: "con-lae", limit: 25 } });
      setMsResults(r.data || []);
    } catch { setMsResults([]); }
  };
  const msPickContrib = async (c) => {
    setMsContrib(c);
    setMsSearch("");
    setMsResults([]);
    setMsResult(null);
    try {
      const r = await axios.get(`${API}/contribuyentes/${c.id}/sedes`, auth);
      setMsSedes(Array.isArray(r.data) ? r.data : []);
    } catch { setMsSedes([]); }
  };
  const msGenerar = async () => {
    if (!msContrib) { toast.error("Selecciona un contribuyente"); return; }
    if (!msDesde) { toast.error("Indica el período desde"); return; }
    if (msSedes.length === 0) { toast.error("Este contribuyente no tiene sedes registradas"); return; }
    setMsLoading(true);
    try {
      const r = await axios.post(`${API}/liquidaciones/generar-por-contribuyente`, {
        contribuyente_id: msContrib.id,
        periodo_desde: msDesde,
        periodo_hasta: msHasta || msDesde,
      }, auth);
      setMsResult(r.data);
      toast.success(`${r.data.total_creadas} liquidaciones creadas · ${r.data.sedes_procesadas} sedes procesadas`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar");
    } finally { setMsLoading(false); }
  };
  const msReset = () => {
    setMsContrib(null); setMsSedes([]); setMsResults([]); setMsSearch(""); setMsResult(null);
  };

  // Aplicar filtros por columna (AND). "contains" case-insensitive; total = prefix numérico.
  const filteredItems = useMemo(() => {
    const norm = (v) => (v ?? "").toString().toLowerCase();
    const match = (val, needle) => !needle || norm(val).includes(norm(needle).trim());
    return items.filter(d => {
      if (!match(d.nro_planilla, colFilters.nro_planilla)) return false;
      if (!match(d.fecha, colFilters.fecha)) return false;
      if (!match(d.rif, colFilters.rif)) return false;
      if (!match(d.nombre_razon_social, colFilters.contribuyente)) return false;
      if (!match(d.mes_anio_declaracion, colFilters.periodo)) return false;
      if (colFilters.total && !norm(d.total).includes(colFilters.total.trim())) return false;
      if (colFilters.pago !== "ALL" && d.estatus_pago !== colFilters.pago) return false;
      if (colFilters.estatus !== "ALL" && d.estatus !== colFilters.estatus) return false;
      return true;
    });
  }, [items, colFilters]);
  const hasActiveFilter = Object.entries(colFilters).some(([k, v]) => v && v !== "ALL");
  const clearFilters = () => setColFilters({ nro_planilla: "", fecha: "", rif: "", contribuyente: "", periodo: "", total: "", pago: "ALL", estatus: "ALL" });
  const setCol = (k, v) => setColFilters(prev => ({ ...prev, [k]: v }));
  useEffect(() => {
    axios.get(`${API}/definiciones/tipos-impuesto`, auth).then(r => setTiposImp(r.data));
    axios.get(`${API}/definiciones/cotizaciones-moneda/activa`, auth)
      .then(r => setCotizacionActiva(r.data && Object.keys(r.data).length ? r.data : null))
      .catch(() => setCotizacionActiva(null));
    axios.get(`${API}/aseo-urbano/tarifas`, auth)
      .then(r => setTarifasAseo(r.data || []))
      .catch(() => setTarifasAseo([]));
    /* eslint-disable-next-line */
  }, []);

  // (búsqueda server-side con debounce ahora unificada en useContribuyentesSearch)

  // TCMMV = valor de la unidad de cuenta activa
  const tcmmv = Number(cotizacionActiva?.valor_unidad_cuenta || 0);

  // Devuelve la tarifa unitaria aplicable a la actividad del contribuyente según m²
  const getTarifaAplicable = (contrib, override = null) => {
    if (!contrib) return { tarifa: 0, actividad: "", area: 0 };
    // override permite pasar {actividad, area} de una sede específica
    const actividad = (override && override.actividad) || contrib.actividad_economica || "";
    const area = Number((override && override.area) ?? contrib.area_total_establecimiento ?? 0);
    const t = tarifasAseo.find(x => (x.actividad_especifica || "").toUpperCase() === actividad.toUpperCase());
    if (!t) return { tarifa: 0, actividad, area };
    // Escalera bifurcada por tipo (industrial / no-industrial)
    const val_or = (v, fallback) => (v == null || v === "" ? fallback : Number(v));
    const tipo = (t.tipo || "no-industrial").toLowerCase();

    let val = 0;
    if (tipo === "industrial") {
      const t1 = Number(t.tarifa_hasta_50 || 0);
      const t2 = val_or(t.tarifa_51_100, t1);
      const t3 = val_or(t.tarifa_101_400, t2);
      const t4 = val_or(t.tarifa_401_700, t3);
      const t5 = val_or(t.tarifa_701_1000, t4);
      const t6 = val_or(t.tarifa_mas_1000, t5);
      if (area <= 50) val = t1;
      else if (area <= 100) val = t2;
      else if (area <= 400) val = t3;
      else if (area <= 700) val = t4;
      else if (area <= 1000) val = t5;
      else val = t6;
    } else {
      const t1 = Number(t.tarifa_hasta_150 || 0);
      const t2 = Number(t.tarifa_151_300 || 0);
      const t3 = Number(t.tarifa_mas_300 || 0);
      if (area <= 150) val = t1;
      else if (area <= 300) val = t2;
      else val = t3;
    }
    return { tarifa: val, actividad, area };
  };

  const fmt = n => `Bs. ${(Number(n)||0).toLocaleString("es-VE",{maximumFractionDigits:2})}`;

  const onContribChange = async (id) => {
    const c = contribuyentes.find(x => x.id === id);
    if (!c) { setForm({ ...form, contribuyente_id: "" }); setMesesPendientes([]); setPeriodosSeleccionados([]); setContribSel(null); setContribSedes([]); setCreditoDisponible(0); return; }
    setContribSel(c);
    // Cargar crédito fiscal disponible del contribuyente (se descontará automáticamente al generar la planilla)
    try {
      const rc = await axios.get(`${API}/contribuyentes/${c.id}/credito-disponible`, auth);
      setCreditoDisponible(Number(rc.data?.saldo_total || 0));
    } catch { setCreditoDisponible(0); }
    // Cargar sedes (multi-sede LAE): si existen, cada período de la planilla se expandirá en 1 línea por sede
    let sedesLoaded = [];
    try {
      const rs = await axios.get(`${API}/contribuyentes/${c.id}/sedes`, auth);
      sedesLoaded = Array.isArray(rs.data) ? rs.data : [];
    } catch { sedesLoaded = []; }
    setContribSedes(sedesLoaded);
    setForm({
      ...form,
      contribuyente_id: c.id,
      clase_contribuyente: c.clase || "",
      rif: `${c.tipo_documento || ""}-${c.documento || ""}`,
      nombre_razon_social: c.razon_social || c.nombre || "",
      lae: c.numero_lae || "",
      codigo_catastral: c.codigo_catastral || "",
    });
    // === FIX: calcular períodos pendientes usando declaraciones pagadas reales ===
    // El campo estático contribuyente.ultimo_pago_mes/anio NO se actualiza cuando se
    // valida un pago, por eso puede quedar desactualizado. La verdad está en las
    // declaraciones con estatus "Pagada" (periodos_cubiertos).
    let cubiertos = new Set();
    let ultimoMes = c.ultimo_pago_mes;
    let ultimoAnio = c.ultimo_pago_anio;
    try {
      const r = await axios.get(`${API}/liquidaciones/datos-declaracion`, {
        ...auth,
        params: { contribuyente_id: c.id, estatus: "Pagada" },
      });
      const decls = (r.data || []).filter(d => d.estatus === "Pagada");
      const { mesAnio, cubiertos: cSet } = calcularUltimoPeriodoPagado(decls, c.ultimo_pago_mes, c.ultimo_pago_anio);
      cubiertos = cSet;
      if (mesAnio) { ultimoMes = mesAnio.mes; ultimoAnio = mesAnio.anio; }
    } catch (e) { /* fallback al snapshot estático del contribuyente */ }

    // Calcular pendientes desde último pago + 1 hasta hoy y filtrar los ya cubiertos
    const pendientes = computePendientes(ultimoMes, ultimoAnio).filter(p => !cubiertos.has(p.key));
    setMesesPendientes(pendientes);
    setPeriodosSeleccionados([]);
  };

  const togglePeriodo = (idx) => {
    // Prelación: marcar/desmarcar respetando orden cronológico
    if (periodosSeleccionados.includes(idx)) {
      // Al desmarcar uno, se desmarcan también los posteriores (no se puede saltar)
      setPeriodosSeleccionados(periodosSeleccionados.filter(i => i < idx));
    } else {
      // Al marcar uno, se marcan también todos los anteriores
      const nuevos = [];
      for (let i = 0; i <= idx; i++) nuevos.push(i);
      setPeriodosSeleccionados(nuevos);
    }
  };

  // Auto-generación de líneas "Servicio de Aseo Urbano" según períodos seleccionados.
  // MULTI-SEDE: si el contribuyente tiene sedes registradas, se genera 1 línea por sede × período,
  // cada una con su propia tarifa (según actividad + m² de la sede). Si además el contribuyente
  // tiene su propia área declarada, se incluye como "Sede Principal" (matriz) para no perder
  // el establecimiento matriz que suele coincidir con el registro principal.
  // SIN sedes: comportamiento clásico — 1 línea por período usando datos del contribuyente.
  const autoLineas = useMemo(() => {
    if (!contribSel) return [];
    const tieneSedes = Array.isArray(contribSedes) && contribSedes.length > 0;
    if (tieneSedes) {
      // Construir la lista efectiva: [matriz (si tiene área propia)] + sedes registradas
      const items = [];
      const areaContrib = Number(contribSel.area_total_establecimiento || 0);
      if (areaContrib > 0) {
        items.push({
          es_matriz: true,
          nombre: "Sede Principal (Matriz)",
          actividad: contribSel.actividad_economica || "",
          area: areaContrib,
          id: `matriz_${contribSel.id}`,
        });
      }
      contribSedes.forEach((sede, i) => {
        items.push({
          es_matriz: false,
          nombre: sede.nombre_sede || `Sucursal ${i + 1}`,
          actividad: sede.actividad_especifica || contribSel.actividad_economica || "",
          area: Number(sede.metros_cuadrados || sede.area_total_establecimiento || 0),
          id: sede.id,
        });
      });
      const lineas = [];
      periodosSeleccionados.forEach(idx => {
        const p = mesesPendientes[idx];
        items.forEach(it => {
          const { tarifa } = getTarifaAplicable(contribSel, { actividad: it.actividad, area: it.area });
          const subTotal = tcmmv * tarifa;
          lineas.push({
            impuesto_tasa: `Aseo Urbano · ${it.nombre} · ${p.mes} ${p.anio}`,
            ingreso_bruto: tcmmv,
            importe: tarifa,
            sub_total: Number(subTotal.toFixed(2)),
            auto: true,
            periodo_key: p.key,
            sede_id: it.id,
            sede_nombre: it.nombre,
            actividad_aplicada: it.actividad,
            area_aplicada: it.area,
          });
        });
      });
      return lineas;
    }
    // Sin sedes: comportamiento original
    const { tarifa, actividad, area } = getTarifaAplicable(contribSel);
    return periodosSeleccionados.map(idx => {
      const p = mesesPendientes[idx];
      const subTotal = tcmmv * tarifa;
      return {
        impuesto_tasa: `Servicio de Aseo Urbano ${p.mes} ${p.anio}`,
        ingreso_bruto: tcmmv,
        importe: tarifa,
        sub_total: Number(subTotal.toFixed(2)),
        auto: true,
        periodo_key: p.key,
        actividad_aplicada: actividad,
        area_aplicada: area,
      };
    });
  }, [periodosSeleccionados, contribSel, contribSedes, tcmmv, tarifasAseo, mesesPendientes]);

  // Líneas manuales (las que NO son auto-generadas)
  const lineasManuales = (form.lineas_impuestos || []).filter(l => !l.auto);
  // Líneas completas mostradas al usuario (auto primero, manuales después)
  const lineasCompletas = [...autoLineas, ...lineasManuales];

  const totalCalculado = () => {
    const sumLineas = lineasCompletas.reduce((a, l) => a + (Number(l.sub_total) || 0), 0);
    return sumLineas + (Number(form.multa) || 0) + (Number(form.retencion) || 0) - (Number(form.descuento) || 0) - (Number(form.reintegro) || 0);
  };

  // Divisas: total / tasa de cambio activa (USD y EUR del submódulo Cotización Moneda)
  const totalBs = totalCalculado();
  const tasaUSD = Number(cotizacionActiva?.dolar || 0);
  const tasaEUR = Number(cotizacionActiva?.euro || 0);
  const divisasUSD = tasaUSD > 0 ? totalBs / tasaUSD : 0;
  const divisasEUR = tasaEUR > 0 ? totalBs / tasaEUR : 0;

  const addLinea = () => {
    const lin = {
      impuesto_tasa: (lineaInput.impuesto_tasa || "").trim(),
      ingreso_bruto: Number(lineaInput.ingreso_bruto) || 0,
      importe: Number(lineaInput.importe) || 0,
      sub_total: Number(lineaInput.sub_total) || (Number(lineaInput.ingreso_bruto) || 0) * (Number(lineaInput.importe) || 0),
      auto: false,
    };
    if (!lin.impuesto_tasa) { toast.error("Indique el impuesto/tasa"); return; }
    setForm({ ...form, lineas_impuestos: [...lineasManuales, lin] });
    setLineaInput({ impuesto_tasa: "", ingreso_bruto: "", importe: "", sub_total: "" });
  };
  const removeLinea = (idx) => {
    // idx corresponde a la posición en lineasCompletas; sólo se permite quitar manuales
    const linea = lineasCompletas[idx];
    if (!linea || linea.auto) return;
    setForm({ ...form, lineas_impuestos: lineasManuales.filter(l => l !== linea) });
  };

  const save = async () => {
    if (!form.contribuyente_id) { toast.error("Seleccione un contribuyente"); return; }
    if (mesesPendientes.length > 0 && periodosSeleccionados.length === 0) {
      toast.error("Seleccione al menos un período pendiente"); return;
    }
    try {
      const periodos = periodosSeleccionados.map(i => mesesPendientes[i].key);
      const ultimoSel = periodos.length ? periodos[periodos.length - 1] : "";
      const payload = { ...form, total: totalCalculado(),
        lineas_impuestos: lineasCompletas,
        divisas: Number(divisasUSD.toFixed(2)),
        divisas_euro: Number(divisasEUR.toFixed(2)),
        mes_anio_declaracion: ultimoSel,
        periodos_cubiertos: periodos };
      if (editing) await axios.put(`${API}/liquidaciones/datos-declaracion/${editing.id}`, payload, auth);
      else {
        const r = await axios.post(`${API}/liquidaciones/datos-declaracion`, payload, auth);
        // Confirmar al funcionario si se disparó el email al contribuyente
        const notif = r.data?.notificacion_email;
        if (notif?.dispatched) {
          toast.success(`📧 Enviando email a ${notif.destino} con la planilla adjunta (en background)`);
        } else if (notif && !notif.dispatched && contribSel) {
          toast.info(notif.info || "Sin envío de email");
        }
        // Si se aplicó crédito fiscal, notificarlo explícitamente
        const credAplicado = Number(r.data?.credito_fiscal_aplicado || 0);
        if (credAplicado > 0) {
          toast.success(
            `💰 Crédito fiscal aplicado: Bs. ${credAplicado.toLocaleString("es-VE",{maximumFractionDigits:2})} (registrado en Reintegro).`,
            { duration: 7000 },
          );
        }
      }
      toast.success(editing ? "Declaración actualizada" : `Declaración registrada · ${periodos.length} período(s) · planilla PDF lista para descargar/enviar`);
      setOpen(false); setEditing(null); setForm(emptyDecl); setMesesPendientes([]); setPeriodosSeleccionados([]); setCreditoDisponible(0);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = async (d) => {
    setEditing(d); setForm({ ...emptyDecl, ...d });
    const c = await getContribById(d.contribuyente_id);
    if (c) {
      setContribSel(c);
      // Calcular período pendiente igual que en creación (excluyendo pagadas)
      let cubiertosSet = new Set();
      let ultimoMes = c.ultimo_pago_mes;
      let ultimoAnio = c.ultimo_pago_anio;
      try {
        const r = await axios.get(`${API}/liquidaciones/datos-declaracion`, {
          ...auth,
          params: { contribuyente_id: c.id, estatus: "Pagada" },
        });
        const decls = (r.data || []).filter(x => x.estatus === "Pagada");
        const { mesAnio, cubiertos } = calcularUltimoPeriodoPagado(decls, c.ultimo_pago_mes, c.ultimo_pago_anio);
        cubiertosSet = cubiertos;
        if (mesAnio) { ultimoMes = mesAnio.mes; ultimoAnio = mesAnio.anio; }
      } catch (e) { /* ignore */ }
      // La declaración en edición debe seguir mostrando sus propios períodos aunque
      // estén "cubiertos" (para poder desmarcarlos). Los quitamos del set.
      (d.periodos_cubiertos || []).forEach(p => cubiertosSet.delete(p));
      if (d.mes_anio_declaracion) cubiertosSet.delete(d.mes_anio_declaracion);
      const pendientes = computePendientes(ultimoMes, ultimoAnio).filter(p => !cubiertosSet.has(p.key));
      // Asegurar que los períodos ya cubiertos por esta declaración aparezcan también
      const cubiertosPropios = d.periodos_cubiertos || (d.mes_anio_declaracion ? [d.mes_anio_declaracion] : []);
      cubiertosPropios.forEach(k => {
        if (!pendientes.find(x => x.key === k)) {
          const parsed = ymToMesAnio(k);
          if (parsed) pendientes.push({ mes: parsed.mes, anio: parsed.anio, key: k });
        }
      });
      pendientes.sort((a, b) => a.key.localeCompare(b.key));
      setMesesPendientes(pendientes);
      const indices = pendientes.map((p, i) => cubiertosPropios.includes(p.key) ? i : -1).filter(i => i >= 0);
      setPeriodosSeleccionados(indices);
    } else { setContribSel(null); setMesesPendientes([]); setPeriodosSeleccionados([]); }
    setOpen(true);
  };
  const anular = async (d) => {
    if (!window.confirm(`¿Anular declaración ${d.nro_planilla}?`)) return;
    await axios.delete(`${API}/liquidaciones/datos-declaracion/${d.id}`, auth);
    toast.success("Declaración anulada"); load();
    window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "anular-planilla", id: d.id } }));
  };
  const desanular = async (d) => {
    if (!window.confirm(`¿Reactivar la planilla ${d.nro_planilla}?\n\nVolverá al estatus "Sin pagar" y se sumará de nuevo a la deuda del contribuyente.`)) return;
    try {
      await axios.post(`${API}/liquidaciones/${d.id}/desanular`, {}, auth);
      toast.success(`Planilla ${d.nro_planilla} reactivada (Sin pagar)`);
      load();
      window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "desanular-planilla", id: d.id } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reactivar la planilla");
    }
  };

  // Descarga el PDF de la planilla (endpoint backend genera con QR de pago)
  const downloadPlanilla = async (d) => {
    try {
      const res = await axios.get(`${API}/liquidaciones/datos-declaracion/${d.id}/planilla-pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `planilla_${(d.nro_planilla || "sisaur").replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo generar la planilla");
    }
  };

  // Abre el PDF de la planilla en una pestaña nueva del navegador (sin descargar)
  const verPlanilla = async (d) => {
    try {
      const res = await axios.get(`${API}/liquidaciones/datos-declaracion/${d.id}/planilla-pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("El navegador bloqueó la ventana emergente. Permita popups para ver la planilla.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("No se pudo abrir la planilla");
    }
  };

  // Reenvío manual del email al contribuyente con la planilla adjunta (Resend)
  const reenviarEmail = async (d) => {
    const c = await getContribById(d.contribuyente_id);
    if (!c?.email) { toast.error("El contribuyente no tiene email registrado en su ficha"); return; }
    if (!window.confirm(`¿Enviar la planilla ${d.nro_planilla} por email a ${c.email}?`)) return;
    try {
      const r = await axios.post(`${API}/liquidaciones/${d.id}/notificar-email`, {}, auth);
      const st = r.data?.email_status;
      if (st === "enviado") toast.success(`📧 Email enviado a ${c.email}`);
      else if (st === "ya_enviado") toast.info("Ya se había enviado antes con éxito · no se re-envía (idempotente)");
      else if (st === "sin_email") toast.warning("El contribuyente no tiene email");
      else if (st === "sin_api_key") toast.error("Resend no está configurado");
      else toast.error(`Error: ${r.data?.email_error || st}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al reenviar"); }
  };

  // Abre WhatsApp web con un mensaje pre-rellenado al celular del contribuyente
  const sendWhatsApp = async (d) => {
    const c = await getContribById(d.contribuyente_id);
    const raw = (c?.celular || c?.telefono || "").replace(/\D/g, "");
    if (!raw) { toast.error("El contribuyente no tiene celular registrado"); return; }
    // Asegurar prefijo internacional (Venezuela = 58). Si empieza con 0 lo reemplazamos.
    const phone = raw.startsWith("58") ? raw : (raw.startsWith("0") ? "58" + raw.slice(1) : "58" + raw);
    // Obtener share_token (público, sin auth) — backfill si la declaración es antigua
    let token = d.share_token;
    if (!token) {
      try {
        const r = await axios.get(`${API}/liquidaciones/datos-declaracion/${d.id}/share-token`, auth);
        token = r.data?.share_token;
      } catch (_e) { toast.error("No se pudo generar el enlace público"); return; }
    }
    const pdfUrl = `${API}/public/planilla/${d.id}/pdf?token=${token}`;
    const totalStr = fmt(d.total || 0);
    const msg = `Hola ${d.nombre_razon_social || ""}, le enviamos su planilla de cobro de Aseo Urbano ${d.nro_planilla || ""} por un total de ${totalStr}. Puede descargarla aquí: ${pdfUrl}\n\nGracias por contribuir a una ciudad más limpia. — Palma Plus, Saneamiento Urbano.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const badgeEstatus = (d_or_e) => {
    // Acepta tanto el string directo (antiguo uso) como el objeto declaracion completo
    const isObj = typeof d_or_e === "object" && d_or_e !== null;
    const e = isObj ? d_or_e.estatus : d_or_e;
    if (e === "Anulada") {
      // Tooltip con quién/cuándo/motivo si el objeto trae metadatos de auditoría
      const meta = isObj ? d_or_e : null;
      const tooltip = meta && (meta.anulada_por_nombre || meta.anulada_motivo)
        ? `Anulada por: ${meta.anulada_por_nombre || "—"}\n` +
          `Fecha: ${(meta.anulada_at || "").slice(0, 19).replace("T", " ")}\n` +
          (meta.anulada_nro_comprobante ? `Recibo origen: ${meta.anulada_nro_comprobante}\n` : "") +
          `Motivo: ${meta.anulada_motivo || "—"}`
        : "Anulada";
      return (
        <span className="inline-flex items-center gap-1" title={tooltip}>
          <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Anulada</Badge>
          {meta && (meta.anulada_por_nombre || meta.anulada_motivo) && (
            <InfoIcon className="w-3 h-3 text-rose-500" />
          )}
        </span>
      );
    }
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Registrada</Badge>;
  };
  const badgePago = (e) => e === "Pagado"
    ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Pagado</Badge>
    : <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Sin pagar</Badge>;

  return (
    <div className="space-y-4" data-testid="datos-declaracion">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-green-800 font-medium" data-testid="decl-filter-count">
            Mostrando <b>{filteredItems.length}</b> de <b>{items.length}</b> declaración(es)
          </span>
          {hasActiveFilter && (
            <Button size="sm" variant="outline" onClick={clearFilters} className="border-orange-200 text-orange-700 hover:bg-orange-50 h-8" data-testid="btn-clear-decl-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid="btn-export-liquidaciones"
                variant="outline"
                disabled={!filteredItems.length}
                className="border-green-200 text-green-800 hover:bg-green-50"
                title={filteredItems.length ? `Exportar ${filteredItems.length} declaración(es) filtradas` : "Sin datos para exportar"}
              >
                <Download className="w-4 h-4 mr-1" /> Exportar
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1">
              <button data-testid="export-excel-liquidaciones" onClick={() => exportarLiquidaciones({ formato: "excel", rows: filteredItems })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left">
                <FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel (.xlsx)
              </button>
              <button data-testid="export-pdf-liquidaciones" onClick={() => exportarLiquidaciones({ formato: "pdf", rows: filteredItems })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left">
                <FileText className="w-4 h-4 text-orange-600" /> PDF (.pdf)
              </button>
              <button data-testid="export-json-liquidaciones" onClick={() => exportarLiquidaciones({ formato: "json", rows: filteredItems })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left">
                <FileJson className="w-4 h-4 text-slate-600" /> JSON (.json)
              </button>
            </PopoverContent>
          </Popover>
          <Dialog open={msOpen} onOpenChange={(o) => { setMsOpen(o); if(!o) msReset(); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="btn-generar-multisede"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                title="Generar planilla consolidada por contribuyente (todas sus sedes)"
              >
                <Zap className="w-4 h-4 mr-1" /> Multi-sede
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Generar liquidaciones por contribuyente (multi-sede)
                </DialogTitle>
              </DialogHeader>

              {!msResult && (
                <div className="space-y-4">
                  {/* Paso 1: buscar contribuyente */}
                  {!msContrib && (
                    <div>
                      <Label>Contribuyente con LAE *</Label>
                      <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input
                          data-testid="ms-search-contrib"
                          placeholder="Buscar por RIF, razón social o denominación (mín. 2 caracteres)"
                          className="pl-9"
                          value={msSearch}
                          onChange={(e) => { setMsSearch(e.target.value); msSearchContrib(e.target.value); }}
                        />
                      </div>
                      {msResults.length > 0 && (
                        <div className="mt-2 border rounded-md max-h-60 overflow-y-auto bg-white">
                          {msResults.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => msPickContrib(c)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-sm"
                              data-testid={`ms-result-${c.id}`}
                            >
                              <div className="font-semibold text-slate-800">{c.razon_social || c.nombre || "—"}</div>
                              <div className="text-xs text-slate-500 font-mono">{c.tipo_documento}-{c.documento} · {c.actividad_economica || "—"}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {msSearch.length >= 2 && msResults.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">Sin resultados. Solo se buscan contribuyentes con LAE.</p>
                      )}
                    </div>
                  )}

                  {/* Paso 2: contribuyente seleccionado y sedes */}
                  {msContrib && (
                    <>
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Contribuyente</div>
                          <div className="font-semibold text-slate-900">{msContrib.razon_social || msContrib.nombre}</div>
                          <div className="text-xs text-slate-600 font-mono">{msContrib.tipo_documento}-{msContrib.documento}</div>
                        </div>
                        <button type="button" onClick={msReset} className="text-blue-700 hover:underline text-xs" data-testid="ms-change-contrib">Cambiar</button>
                      </div>

                      <div>
                        <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-emerald-700" />
                          Sedes que se procesarán ({msSedes.length})
                        </div>
                        {msSedes.length === 0 ? (
                          <div className="p-3 border border-dashed border-rose-200 rounded-lg bg-rose-50 text-xs text-rose-700">
                            Este contribuyente aún no tiene sedes registradas. Agrégalas desde el módulo Contribuyentes → Editar.
                          </div>
                        ) : (
                          <div className="border rounded-md max-h-40 overflow-y-auto divide-y">
                            {msSedes.map((s, i) => (
                              <div key={s.id} className="px-3 py-1.5 text-xs flex items-center justify-between" data-testid={`ms-sede-${i}`}>
                                <div className="truncate">
                                  <span className="font-semibold text-slate-800">{s.nombre_sede || `Sede ${i+1}`}</span>
                                  {s.direccion && <span className="text-slate-500"> · {s.direccion}</span>}
                                </div>
                                <span className="font-mono text-slate-600 shrink-0 ml-2">{s.metros_cuadrados || 0} m²</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Período desde *</Label>
                          <Input type="month" value={msDesde} onChange={(e) => setMsDesde(e.target.value)} data-testid="ms-desde" />
                        </div>
                        <div>
                          <Label className="text-xs">Período hasta</Label>
                          <Input type="month" value={msHasta} onChange={(e) => setMsHasta(e.target.value)} data-testid="ms-hasta" />
                          <p className="text-[10px] text-slate-500 mt-0.5">Si no se llena, sólo genera el mes desde.</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Resultado */}
              {msResult && (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 text-green-800 font-semibold text-sm mb-1">
                      <Zap className="w-4 h-4" /> Liquidaciones generadas correctamente
                    </div>
                    <div className="text-xs text-slate-700">
                      <div>Sedes procesadas: <strong>{msResult.sedes_procesadas}</strong></div>
                      <div>Liquidaciones creadas: <strong className="text-green-700">{msResult.total_creadas}</strong></div>
                      <div>Ya existentes (omitidas): <strong>{msResult.total_saltadas}</strong></div>
                    </div>
                  </div>
                  {msResult.detalle_sedes?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-1">Detalle por sede</div>
                      <div className="border rounded-md divide-y max-h-56 overflow-y-auto">
                        {msResult.detalle_sedes.map((d, i) => (
                          <div key={i} className="px-3 py-2 text-xs flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-slate-800">{d.nombre_sede}</div>
                              <div className="text-slate-500">{d.metros_cuadrados} m² · {d.creadas} creadas · {d.ya_existentes} existentes</div>
                            </div>
                            <div className="text-right font-mono text-green-800 font-semibold">Bs. {(d.monto_mensual || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}/mes</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {msResult.errores?.length > 0 && (
                    <div className="border border-rose-200 rounded-md bg-rose-50 p-3">
                      <div className="text-xs font-semibold text-rose-700 mb-1">Sedes con errores ({msResult.errores.length})</div>
                      <ul className="text-xs text-rose-700 list-disc list-inside">
                        {msResult.errores.map((e, i) => <li key={i}><strong>{e.sede || "—"}</strong>: {e.motivo}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                {msResult ? (
                  <Button onClick={() => setMsOpen(false)} data-testid="ms-close" className="bg-green-700 hover:bg-green-800 text-white">Cerrar</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setMsOpen(false)}>Cancelar</Button>
                    <Button
                      onClick={msGenerar}
                      disabled={!msContrib || msSedes.length === 0 || !msDesde || msLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="ms-generar"
                    >
                      {msLoading ? "Generando..." : <><Zap className="w-4 h-4 mr-1" />Generar liquidaciones</>}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if(!o){ setEditing(null); setForm(emptyDecl); setMesesPendientes([]); setPeriodosSeleccionados([]); setContribSel(null); setContribSearch(""); setContribSedes([]); } }}>
            <DialogTrigger asChild><Button data-testid="btn-add-decl" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nueva Declaración</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? `Editar declaración ${editing.nro_planilla}` : "Nueva declaración"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Contribuyente*</Label>
                <ContribuyenteCombobox
                  value={form.contribuyente_id}
                  onChange={(id) => onContribChange(id)}
                  contribuyentes={contribuyentes}
                  search={contribSearch}
                  onSearchChange={setContribSearch}
                  placeholder="Seleccione contribuyente"
                  testId="decl-contrib"
                />
              </div>
              <div><Label>RIF</Label><Input value={form.rif||""} onChange={e => setForm({...form, rif:e.target.value})} /></div>
              <div><Label>Razón Social / Nombre</Label><Input value={form.nombre_razon_social||""} onChange={e => setForm({...form, nombre_razon_social:e.target.value})} /></div>
              <div><Label>Código catastral</Label><Input value={form.codigo_catastral||""} onChange={e => setForm({...form, codigo_catastral:e.target.value})} /></div>
              <div><Label>Fecha</Label><Input data-testid="decl-fecha" type="date" value={form.fecha||""} onChange={e => setForm({...form, fecha:e.target.value})} /></div>
              <div className="col-span-2 border-t pt-3 mt-2">
                <p className="font-semibold text-green-900 mb-1">Períodos pendientes de pago</p>
                {!form.contribuyente_id && <p className="text-xs text-slate-500">Seleccione un contribuyente para ver los meses adeudados.</p>}
                {form.contribuyente_id && mesesPendientes.length === 0 && (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-md p-2">✓ Este contribuyente está al día con el pago del aseo.</p>
                )}
                {mesesPendientes.length > 0 && (
                  <>
                    <p className="text-xs text-slate-500 mb-2">Calculados desde el &quot;Último Mes de Pago del Aseo&quot; del contribuyente. Hay <strong>prelación</strong>: al seleccionar un mes se marcan automáticamente todos los anteriores. No se puede saltar períodos.</p>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-green-50/40 border border-green-100 rounded-lg" data-testid="checklist-periodos">
                      {mesesPendientes.map((p, idx) => {
                        const checked = periodosSeleccionados.includes(idx);
                        return (
                          <label key={p.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer transition ${checked ? "bg-orange-100 border-orange-300" : "bg-white border-green-100 hover:border-green-300"}`} data-testid={`periodo-${p.key}`}>
                            <input type="checkbox" checked={checked} onChange={() => togglePeriodo(idx)} className="accent-orange-500 w-4 h-4" />
                            <span className="text-sm font-medium text-green-900">{p.mes} {p.anio}</span>
                          </label>
                        );
                      })}
                    </div>
                    {periodosSeleccionados.length > 0 && (
                      <p className="text-xs text-orange-700 mt-2 font-semibold">{periodosSeleccionados.length} período(s) seleccionado(s) — hasta {mesesPendientes[periodosSeleccionados[periodosSeleccionados.length - 1]].mes} {mesesPendientes[periodosSeleccionados[periodosSeleccionados.length - 1]].anio}</p>
                    )}
                  </>
                )}
              </div>
              <div><Label>Estatus de pago</Label>
                <Select value={form.estatus_pago} onValueChange={v => setForm({...form, estatus_pago:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Sin pagar">Sin pagar</SelectItem><SelectItem value="Pagado">Pagado</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Estatus</Label>
                <Select value={form.estatus} onValueChange={v => setForm({...form, estatus:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Registrada">Registrada</SelectItem><SelectItem value="Anulada">Anulada</SelectItem></SelectContent>
                </Select>
              </div>

              <div className="col-span-2 border-t pt-3 mt-2">
                <p className="font-semibold text-green-900 mb-2">Líneas de impuestos / tasas</p>
                {contribSedes.length > 0 && (() => {
                  const areaContrib = Number(contribSel?.area_total_establecimiento || 0);
                  const incluyeMatriz = areaContrib > 0;
                  const totalUbicaciones = contribSedes.length + (incluyeMatriz ? 1 : 0);
                  const items = [];
                  if (incluyeMatriz) items.push({ id: "matriz", nombre: "Matriz", actividad: contribSel.actividad_economica, area: areaContrib });
                  contribSedes.forEach((s, i) => items.push({ id: s.id, nombre: s.nombre_sede || `Sucursal ${i+1}`, actividad: s.actividad_especifica || contribSel.actividad_economica, area: Number(s.metros_cuadrados || 0) }));
                  return (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded-md p-2 mb-2 flex items-start gap-2" data-testid="multi-sede-alert">
                      <Building2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-blue-900">Contribuyente multi-sede ({totalUbicaciones} ubicaciones){incluyeMatriz ? " · incluye matriz" : ""}</div>
                        <div className="text-slate-600 mt-0.5">Se generará <strong>1 línea por ubicación × período seleccionado</strong>. Cada una calcula su tarifa según su actividad y m². Total = suma de todas.</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {items.map((it) => {
                            const tf = getTarifaAplicable(contribSel, { actividad: it.actividad, area: it.area });
                            return (
                              <span key={it.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-blue-200 font-mono text-[10px]">
                                <span className="font-semibold text-slate-800">{it.nombre}</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-slate-600">{it.area || 0} m²</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-green-700 font-bold">Bs. {(tcmmv * tf.tarifa).toLocaleString('es-VE',{maximumFractionDigits:2})}/mes</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {(contribSel || tcmmv > 0) && contribSedes.length === 0 && (
                  <div className="text-xs bg-orange-50 border border-orange-100 rounded-md p-2 mb-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="auto-context-info">
                    <div><span className="text-slate-500">TCMMV:</span> <strong className="text-orange-700 font-mono">{tcmmv ? tcmmv.toLocaleString("es-VE",{maximumFractionDigits:4}) : "—"}</strong></div>
                    <div><span className="text-slate-500">Actividad:</span> <strong className="text-green-900">{contribSel?.actividad_economica || "—"}</strong></div>
                    <div><span className="text-slate-500">Área (m²):</span> <strong className="text-green-900 font-mono">{contribSel?.area_total_establecimiento ?? "—"}</strong></div>
                    <div><span className="text-slate-500">Tarifa unitaria:</span> <strong className="text-green-900 font-mono">{contribSel ? getTarifaAplicable(contribSel).tarifa.toLocaleString("es-VE",{maximumFractionDigits:2}) : "—"}</strong></div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mb-2">Las líneas marcadas con <span className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">AUTO</span> se generan automáticamente desde los períodos seleccionados. Fórmula: <span className="font-mono">TCMMV × tarifa</span> (la tarifa se selecciona según el tramo de m² del inmueble).</p>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">Impuesto / Tasa (manual)</Label>
                    <Select value={lineaInput.impuesto_tasa} onValueChange={v => {
                      const t = tiposImp.find(x => x.nombre === v);
                      const tarifa = Number(t?.tasa || 0);
                      const sub = tcmmv * tarifa;
                      setLineaInput({
                        impuesto_tasa: v,
                        ingreso_bruto: tcmmv ? String(tcmmv) : "",
                        importe: tarifa ? String(tarifa) : "",
                        sub_total: sub ? sub.toFixed(2) : "",
                      });
                    }}>
                      <SelectTrigger data-testid="linea-impuesto"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                      <SelectContent>
                        {tiposImp.length === 0
                          ? <SelectItem disabled value="_empty">Sin tipos. Registre en Definiciones</SelectItem>
                          : tiposImp.map(t => <SelectItem key={t.id} value={t.nombre}>{t.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3"><Label className="text-xs">TCMMV</Label><Input type="number" step="0.01" value={lineaInput.ingreso_bruto} onChange={e => {
                    const v = e.target.value;
                    const sub = (Number(v) || 0) * (Number(lineaInput.importe) || 0);
                    setLineaInput({...lineaInput, ingreso_bruto: v, sub_total: sub ? sub.toFixed(2) : ""});
                  }} /></div>
                  <div className="col-span-2"><Label className="text-xs">Tarifa</Label><Input type="number" step="0.01" value={lineaInput.importe} onChange={e => {
                    const v = e.target.value;
                    const sub = (Number(lineaInput.ingreso_bruto) || 0) * (Number(v) || 0);
                    setLineaInput({...lineaInput, importe: v, sub_total: sub ? sub.toFixed(2) : ""});
                  }} /></div>
                  <div className="col-span-2"><Label className="text-xs">Sub-Total <span className="text-[10px] text-slate-400">· auto</span></Label><Input type="number" step="0.01" value={lineaInput.sub_total} readOnly disabled className="bg-slate-50" placeholder="Auto" /></div>
                  <Button type="button" data-testid="btn-add-linea" onClick={addLinea} className="col-span-1 bg-green-700 hover:bg-green-800 text-white"><Plus className="w-4 h-4" /></Button>
                </div>
                {lineasCompletas.length > 0 && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-green-50 text-green-900"><tr>
                        <th className="text-left p-1.5">Impuesto / Tasa</th>
                        <th className="text-right p-1.5">TCMMV</th>
                        <th className="text-right p-1.5">Tarifa</th>
                        <th className="text-right p-1.5">Sub-Total</th>
                        <th className="text-right p-1.5">×</th>
                      </tr></thead>
                      <tbody>{lineasCompletas.map((l, idx) => (
                        <tr key={idx} className={`border-b border-green-50 ${l.auto ? "bg-orange-50/40" : ""}`} data-testid={`linea-row-${idx}`}>
                          <td className="p-1.5">
                            {l.auto && <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold align-middle">AUTO</span>}
                            {l.impuesto_tasa}
                          </td>
                          <td className="p-1.5 text-right font-mono">{Number(l.ingreso_bruto).toFixed(2)}</td>
                          <td className="p-1.5 text-right font-mono">{Number(l.importe).toFixed(2)}</td>
                          <td className="p-1.5 text-right font-mono font-semibold">{Number(l.sub_total).toFixed(2)}</td>
                          <td className="p-1.5 text-right">{!l.auto && <Button size="icon" variant="ghost" onClick={() => removeLinea(idx)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="col-span-2 border-t pt-3 mt-2"><p className="font-semibold text-green-900 mb-2">Otros conceptos</p></div>
              <div className="col-span-2 mt-1"><Label className="text-xs text-green-800 flex items-center gap-2">
                Ajustes {!canEditAjustes && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">🔒 solo Superadmin / Director de Hacienda</span>}
              </Label></div>
              <div><Label>Descuento</Label><Input type="number" step="0.01" data-testid="input-descuento" value={form.descuento} disabled={!canEditAjustes} onChange={e => setForm({...form, descuento:e.target.value})} /></div>
              <div><Label>Reintegro <span className="text-[10px] text-emerald-700">{creditoDisponible > 0 ? "· crédito auto" : ""}</span></Label><Input type="number" step="0.01" data-testid="input-reintegro" value={form.reintegro} disabled={!canEditAjustes} onChange={e => setForm({...form, reintegro:e.target.value})} /></div>
              <div><Label>Retención</Label><Input type="number" step="0.01" data-testid="input-retencion" value={form.retencion} disabled={!canEditAjustes} onChange={e => setForm({...form, retencion:e.target.value})} /></div>
              <div><Label>Multa</Label><Input type="number" step="0.01" data-testid="input-multa" value={form.multa} disabled={!canEditAjustes} onChange={e => setForm({...form, multa:e.target.value})} /></div>
              <div><Label className="text-slate-500">Divisas (USD) <span className="text-[10px] text-slate-400">· auto</span></Label><Input data-testid="decl-divisas-usd" type="number" step="0.01" value={divisasUSD.toFixed(2)} readOnly disabled className="bg-slate-50" /></div>
              <div><Label className="text-slate-500">Divisas (EUR) <span className="text-[10px] text-slate-400">· auto</span></Label><Input data-testid="decl-divisas-eur" type="number" step="0.01" value={divisasEUR.toFixed(2)} readOnly disabled className="bg-slate-50" /></div>

              <div className="col-span-2 mt-2 p-3 bg-orange-50 border border-orange-100 rounded-lg flex items-center justify-between">
                <span className="font-semibold text-orange-900">TOTAL CALCULADO</span>
                <span className="font-display font-extrabold text-2xl text-orange-700">{fmt(totalCalculado())}</span>
              </div>
              {creditoDisponible > 0 && (
                <div
                  className="col-span-2 mt-1 p-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg"
                  data-testid="decl-credito-fiscal-banner"
                >
                  <p className="text-sm text-emerald-900">
                    💰 <b>Saldo a favor del contribuyente:</b>{" "}
                    <span className="font-mono font-bold" data-testid="decl-credito-monto">{fmt(creditoDisponible)}</span>
                  </p>
                  <p className="text-[11.5px] text-emerald-800 mt-1 leading-snug">
                    Al registrar la planilla, este crédito fiscal se descontará automáticamente y quedará
                    reflejado en el campo <b>Reintegro</b>. Si el crédito es menor al total, el resto queda
                    como saldo del contribuyente para próximas planillas.
                  </p>
                  <p className="text-[10.5px] text-emerald-700 mt-1">
                    <b>Total estimado a pagar tras descuento:</b>{" "}
                    <span className="font-mono">
                      {fmt(Math.max(0, totalCalculado() - Math.min(creditoDisponible, totalCalculado())))}
                    </span>
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-decl" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editing ? "Actualizar" : "Registrar"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4 border-green-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900"><tr>
              <th className="text-left p-2.5">N° Planilla</th><th className="text-left p-2.5">Fecha</th><th className="text-left p-2.5">RIF</th>
              <th className="text-left p-2.5">Contribuyente</th><th className="text-left p-2.5">Período</th>
              <th className="text-right p-2.5">Total</th><th className="text-center p-2.5">Pago</th><th className="text-center p-2.5">Estatus</th><th className="text-right p-2.5">Acciones</th>
            </tr>
            {/* Fila de filtros por columna (multi-filtro AND) */}
            <tr className="bg-green-50/60 border-b border-green-100">
              <th className="p-1.5"><Input data-testid="colf-nro-planilla" value={colFilters.nro_planilla} onChange={e => setCol("nro_planilla", e.target.value)} placeholder="PLN-..." className="h-8 text-xs font-mono bg-white" /></th>
              <th className="p-1.5"><Input data-testid="colf-fecha" value={colFilters.fecha} onChange={e => setCol("fecha", e.target.value)} placeholder="2026-06" className="h-8 text-xs bg-white" /></th>
              <th className="p-1.5"><Input data-testid="colf-rif" value={colFilters.rif} onChange={e => setCol("rif", e.target.value)} placeholder="J-XXXXXXX" className="h-8 text-xs font-mono bg-white" /></th>
              <th className="p-1.5"><Input data-testid="colf-contrib" value={colFilters.contribuyente} onChange={e => setCol("contribuyente", e.target.value)} placeholder="Razón social..." className="h-8 text-xs bg-white" /></th>
              <th className="p-1.5"><Input data-testid="colf-periodo" value={colFilters.periodo} onChange={e => setCol("periodo", e.target.value)} placeholder="YYYY-MM" className="h-8 text-xs font-mono bg-white" /></th>
              <th className="p-1.5"><Input data-testid="colf-total" value={colFilters.total} onChange={e => setCol("total", e.target.value)} placeholder="Ej: 1200" className="h-8 text-xs text-right bg-white" /></th>
              <th className="p-1.5">
                <Select value={colFilters.pago} onValueChange={v => setCol("pago", v)}>
                  <SelectTrigger className="h-8 text-xs bg-white" data-testid="colf-pago"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="Pagado">Pagado</SelectItem>
                    <SelectItem value="Sin pagar">Sin pagar</SelectItem>
                  </SelectContent>
                </Select>
              </th>
              <th className="p-1.5">
                <Select value={colFilters.estatus} onValueChange={v => setCol("estatus", v)}>
                  <SelectTrigger className="h-8 text-xs bg-white" data-testid="colf-estatus"><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todas</SelectItem>
                    <SelectItem value="Registrada">Registrada</SelectItem>
                    <SelectItem value="Anulada">Anulada</SelectItem>
                  </SelectContent>
                </Select>
              </th>
              <th className="p-1.5 text-right">
                {hasActiveFilter && (
                  <Button size="icon" variant="ghost" onClick={clearFilters} title="Limpiar todos los filtros" data-testid="colf-clear" className="h-8 w-8">
                    <X className="w-4 h-4 text-orange-600" />
                  </Button>
                )}
              </th>
            </tr>
            </thead>
            <tbody>{filteredItems.map(d => (
              <tr key={d.id} className="border-b border-green-50 hover:bg-green-50/40" data-testid={`row-decl-${d.nro_planilla}`}>
                <td className="p-2.5 font-mono text-xs">{d.nro_planilla}</td>
                <td className="p-2.5 text-xs">{d.fecha||"-"}</td>
                <td className="p-2.5 font-mono">{d.rif||"-"}</td>
                <td className="p-2.5">{d.nombre_razon_social||"-"}</td>
                <td className="p-2.5 font-mono text-xs">{d.mes_anio_declaracion||"-"}</td>
                <td className="p-2.5 text-right font-semibold">{fmt(d.total)}</td>
                <td className="p-2.5 text-center">{badgePago(d.estatus_pago)}</td>
                <td className="p-2.5 text-center">{badgeEstatus(d)}</td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => verPlanilla(d)} title="Ver planilla" data-testid={`btn-ver-${d.nro_planilla}`}><FileSearch className="w-4 h-4 text-orange-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => downloadPlanilla(d)} title="Descargar planilla" data-testid={`btn-download-${d.nro_planilla}`}><Download className="w-4 h-4 text-green-700" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => sendWhatsApp(d)} title="Enviar por WhatsApp" data-testid={`btn-whatsapp-${d.nro_planilla}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => reenviarEmail(d)} title="Reenviar email al contribuyente" data-testid={`btn-email-${d.nro_planilla}`}><Mail className="w-4 h-4 text-cyan-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => edit(d)} title="Editar"><Edit className="w-4 h-4 text-blue-600" /></Button>
                  {d.estatus === "Registrada" && <Button size="icon" variant="ghost" onClick={() => anular(d)} title="Anular"><Ban className="w-4 h-4 text-orange-600" /></Button>}
                  {d.estatus === "Anulada" && hasPermission && hasPermission("recaudaciones.anular") && (
                    <Button size="icon" variant="ghost" onClick={() => desanular(d)} title="Reactivar planilla (volver a Sin pagar)" data-testid={`btn-desanular-${d.nro_planilla}`}>
                      <RotateCcw className="w-4 h-4 text-emerald-700" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}{!filteredItems.length && <tr><td colSpan="9" className="p-8 text-center text-slate-400">
              {items.length ? "Sin resultados que coincidan con los filtros aplicados." : "Sin declaraciones registradas"}
            </td></tr>}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
